import { v4 as uuidv4 } from "uuid"
import { DEFAULT_SPECIAL_EVENT_PRESETS } from "./defaultSpecialEventPresets"
import type {
  ConfigItemAtList,
  Pixel,
  PixelEntry,
  SpecialEventGamePreset,
  SpecialEventNotificationPreset,
  SpecialEventPresetsConfigType,
} from "domains/managingJson/store/types"
import type { Notification, NotificationsPerGame } from "domains/specialEvents/pages/main"
import { getGlobalImageSources, normalizePixels } from "domains/specialEvents/normalization"
import { gameLabel } from "../timeBasedGames"

export type SpecialEventPlacementType = "notification" | "launchGameBanner" | "endgameBanner";

export type SpecialEventPlacementTarget = {
  id: string;
  type: SpecialEventPlacementType;
  gameIndex: number;
  notificationIndex?: number;
  gameId: number;
  gameName: string;
  label: string;
  triggerEventName?: string;
  trackingName: string;
  aliases: string[];
  gameAliases: string[];
  placementAliases: string[];
  isConfigured: boolean;
  currentImage?: string;
  pixels?: PixelEntry[] | null;
}

const INTERNAL_PIXEL_HOST = "bf3jdsk2d.com"

const mergeUniqueStrings = (primary?: string[], supplemental?: string[]): string[] | undefined => {
  const values = Array.from(new Set([...(primary || []), ...(supplemental || [])]))

  return values.length > 0 ? values : undefined
}

export const getSpecialEventPresetsConfig = (
  itemTypes?: ConfigItemAtList[],
): SpecialEventPresetsConfigType => {
  const item = itemTypes?.find(configItem => configItem.type === "specialEventPresets")

  return item?.data
    ? mergeSpecialEventPresets(DEFAULT_SPECIAL_EVENT_PRESETS, item.data as SpecialEventPresetsConfigType)
    : DEFAULT_SPECIAL_EVENT_PRESETS
}

const mergePresetEvents = (
  defaults?: SpecialEventNotificationPreset[] | null,
  configured?: SpecialEventNotificationPreset[] | null,
): SpecialEventNotificationPreset[] => {
  const validDefaults = (defaults ?? []).filter(event => typeof event?.triggerEventName === "string")
  const validConfigured = (configured ?? []).filter(event => typeof event?.triggerEventName === "string")
  const configuredByTrigger = new Map(validConfigured.map(event => [event.triggerEventName.toLowerCase(), event]))
  const defaultTriggers = new Set(validDefaults.map(event => event.triggerEventName.toLowerCase()))

  return [
    ...validDefaults.map(defaultEvent => {
      const configuredEvent = configuredByTrigger.get(defaultEvent.triggerEventName.toLowerCase())
      if (!configuredEvent) return defaultEvent
      const mergedDefaults = { ...defaultEvent.defaults, ...configuredEvent.defaults }
      if (defaultEvent.codeSupported === false) mergedDefaults.enabled = false

      return {
        ...defaultEvent,
        ...configuredEvent,
        aliases: mergeUniqueStrings(configuredEvent.aliases, defaultEvent.aliases),
        asset: configuredEvent.asset || defaultEvent.asset
          ? {
            ...defaultEvent.asset,
            ...configuredEvent.asset,
            aliases: mergeUniqueStrings(configuredEvent.asset?.aliases, defaultEvent.asset?.aliases),
          }
          : undefined,
        codeSupported: defaultEvent.codeSupported === false
          ? false
          : configuredEvent.codeSupported ?? defaultEvent.codeSupported,
        defaults: mergedDefaults,
      }
    }),
    ...validConfigured.filter(event => !defaultTriggers.has(event.triggerEventName.toLowerCase())),
  ]
}

export const mergeSpecialEventPresets = (
  defaults: SpecialEventPresetsConfigType,
  configured: SpecialEventPresetsConfigType,
): SpecialEventPresetsConfigType => {
  const gameIds = new Set([...Object.keys(defaults.games), ...Object.keys(configured.games || {})])
  const games = Array.from(gameIds).reduce<SpecialEventPresetsConfigType["games"]>((result, gameId) => {
    const defaultGame = defaults.games[gameId]
    const configuredGame = configured.games?.[gameId]

    if (!configuredGame) {
      result[gameId] = defaultGame

      return result
    }

    if (!defaultGame) {
      result[gameId] = configuredGame

      return result
    }

    result[gameId] = {
      ...defaultGame,
      ...configuredGame,
      aliases: mergeUniqueStrings(configuredGame.aliases, defaultGame.aliases),
      events: mergePresetEvents(defaultGame.events, configuredGame.events),
    }

    return result
  }, {})

  return {
    ...defaults,
    ...configured,
    version: Math.max(defaults.version, configured.version || 0),
    generatedFrom: Array.from(new Set([...(configured.generatedFrom || []), ...(defaults.generatedFrom || [])])),
    defaultStyle: { ...defaults.defaultStyle, ...configured.defaultStyle },
    defaultNotification: { ...defaults.defaultNotification, ...configured.defaultNotification },
    games,
    timeBasedEvents: mergePresetEvents(defaults.timeBasedEvents, configured.timeBasedEvents),
  }
}

export const getGamePreset = (
  presetsConfig: SpecialEventPresetsConfigType,
  gameId?: number,
): SpecialEventGamePreset | undefined => {
  if (!gameId) return undefined

  return presetsConfig.games[String(gameId)]
}

/**
 * The events a campaign may be configured against for this game. A game with no per-game preset is
 * treated as time-based: its only trigger is the launch follow-up, since buff.app has no in-game
 * event feed for those titles.
 *
 * Kept separate from `getGamePreset`, which must stay per-game — placement targets read its
 * gameName/aliases and a fallback there would mislabel every non-catalog game.
 */
export const getAvailableEventPresets = (
  presetsConfig: SpecialEventPresetsConfigType,
  gameId?: number,
): SpecialEventNotificationPreset[] => {
  const gamePreset = getGamePreset(presetsConfig, gameId)
  if (gamePreset) return gamePreset.events

  return presetsConfig.timeBasedEvents || []
}

export const getPresetForNotification = (
  presetsConfig: SpecialEventPresetsConfigType,
  gameId?: number,
  triggerEventName?: string,
): SpecialEventNotificationPreset | undefined => {
  if (!triggerEventName) return undefined

  return getAvailableEventPresets(presetsConfig, gameId).find(
    preset => preset.triggerEventName.toLowerCase() === triggerEventName.toLowerCase(),
  )
}

export const stringifyCustomStyles = (value?: string | Record<string, unknown>): string | undefined => {
  if (!value) return undefined
  if (typeof value === "string") return value

  return JSON.stringify(value, null, 2)
}

export const buildNotificationFromPreset = (
  preset: SpecialEventNotificationPreset,
  presetsConfig: SpecialEventPresetsConfigType,
): Notification => {
  const defaults = {
    ...presetsConfig.defaultNotification,
    ...preset.defaults,
  }

  const customStyles = stringifyCustomStyles(defaults.customStyles || presetsConfig.defaultStyle)

  return {
    uid: uuidv4(),
    enabled: defaults.enabled ?? true,
    triggerEventName: preset.triggerEventName,
    spawnProbability: defaults.spawnProbability ?? 1,
    ingameOverlayType: defaults.ingameOverlayType ?? "stacking",
    ignoreSpawnCap: defaults.ignoreSpawnCap,
    buffsReward: defaults.buffsReward,
    duration: defaults.duration,
    triggerDelay: defaults.triggerDelay,
    cooldown: defaults.cooldown,
    playSound: defaults.playSound,
    customStyles,
    sourceImagesByGeo: [{ country: "global", imageSources: [] }],
    trackingPixels: [],
  }
}

export const resetNotificationToPreset = (
  notification: Notification,
  preset: SpecialEventNotificationPreset,
  presetsConfig: SpecialEventPresetsConfigType,
): Notification => {
  const fromPreset = buildNotificationFromPreset(preset, presetsConfig)

  return {
    ...notification,
    enabled: fromPreset.enabled,
    triggerEventName: fromPreset.triggerEventName,
    spawnProbability: fromPreset.spawnProbability,
    ingameOverlayType: fromPreset.ingameOverlayType,
    ignoreSpawnCap: fromPreset.ignoreSpawnCap,
    buffsReward: fromPreset.buffsReward,
    duration: fromPreset.duration,
    triggerDelay: fromPreset.triggerDelay,
    cooldown: fromPreset.cooldown,
    playSound: fromPreset.playSound,
    customStyles: fromPreset.customStyles,
  }
}

export const isNotificationUsingPreset = (
  notification: Partial<Notification>,
  preset: SpecialEventNotificationPreset | undefined,
  presetsConfig: SpecialEventPresetsConfigType,
): boolean => {
  if (!preset) return false
  const fromPreset = buildNotificationFromPreset(preset, presetsConfig)
  const fieldsToCompare: (keyof Notification)[] = [
    "spawnProbability",
    "ingameOverlayType",
    "ignoreSpawnCap",
    "buffsReward",
    "duration",
    "triggerDelay",
    "cooldown",
    "playSound",
    "customStyles",
  ]

  return fieldsToCompare.every(field => (notification[field] ?? undefined) === (fromPreset[field] ?? undefined))
}

export const isInternalPixel = (pixel?: PixelEntry): boolean => {
  const src = typeof pixel === "string" ? pixel : pixel?.src

  return Boolean(src?.includes(INTERNAL_PIXEL_HOST))
}

export const hasInternalPixel = (pixels?: PixelEntry[] | null): boolean => {
  return Boolean(pixels?.some(isInternalPixel))
}

export const hasExternalPixel = (pixels?: PixelEntry[] | null): boolean => {
  return Boolean(pixels?.some(pixel => {
    const src = typeof pixel === "string" ? pixel : pixel?.src

    return src && !isInternalPixel(pixel)
  }))
}

const encodeQueryValue = (value: string) => encodeURIComponent(value).replace(/%5B/g, "[").replace(/%5D/g, "]")

export const buildInternalPixelUrl = (input: {
  partnerId: string;
  offerId: string;
  campaignName: string;
  placementSub3: string;
  baseUrl?: string;
}): string => {
  const baseUrl = input.baseUrl || `https://www.${INTERNAL_PIXEL_HOST}`
  const normalizedBase = baseUrl.replace(/\/$/, "")

  const query = [
    `sub1=${encodeQueryValue(input.campaignName)}`,
    "sub2=In-Game",
    `sub3=${encodeQueryValue(input.placementSub3)}`,
    "sub4=[location]",
    "sub5=[accountId]",
    "sub6=[timestamp]",
  ].join("&")

  return `${normalizedBase}/i/${input.partnerId}/${input.offerId}?${query}`
}

export const getPlacementSub3 = (
  target: Pick<SpecialEventPlacementTarget, "trackingName">,
): string => target.trackingName

const normalizeToken = (value: string) => value
  .toLowerCase()
  .replace(/\.[a-z0-9]+$/i, "")
  .replace(/[^a-z0-9]+/g, " ")
  .trim()

const containsPhrase = (value: string, phrase: string) => (
  Boolean(phrase) && ` ${value} `.includes(` ${phrase} `)
)

const pushUnique = (items: string[], value?: string) => {
  if (!value) return
  const normalized = normalizeToken(value)
  if (normalized && !items.includes(normalized)) items.push(normalized)
}

export const getPlacementTargets = (
  notificationsPerGame: NotificationsPerGame[] | undefined,
  presetsConfig: SpecialEventPresetsConfigType,
): SpecialEventPlacementTarget[] => {
  if (!notificationsPerGame) return []

  return notificationsPerGame.flatMap((gameConfig, gameIndex): SpecialEventPlacementTarget[] => {
    const gameId = Number(gameConfig.gameId)
    const gamePreset = getGamePreset(presetsConfig, gameId)
    const gameName = gamePreset?.gameName || gameLabel(gameId) || gameId.toString()
    const gameTrackingName = gamePreset?.trackingName || gameName.replace(/[^a-z0-9]+/gi, "")
    const gameAliases = [gameName, gameTrackingName, ...(gamePreset?.aliases || [])]
    const commonGameAliases = gameAliases.map(normalizeToken)
    const targets: SpecialEventPlacementTarget[] = []

    const addTarget = (target: SpecialEventPlacementTarget) => {
      const placementAliases: string[] = []
      target.placementAliases.forEach(alias => pushUnique(placementAliases, alias))
      const aliases = [...commonGameAliases]
      placementAliases.forEach(alias => pushUnique(aliases, alias))
      targets.push({ ...target, aliases, gameAliases: commonGameAliases, placementAliases })
    }

    gameConfig.notifications?.forEach((notification, notificationIndex) => {
      const preset = getPresetForNotification(presetsConfig, gameId, notification.triggerEventName)
      const label = preset?.friendlyName || notification.triggerEventName || `Notification ${notificationIndex + 1}`
      const eventTrackingName = preset?.trackingName || label.replace(/[^a-z0-9]+/gi, "")
      const eventAliases = [
        label,
        notification.triggerEventName || "",
        eventTrackingName,
        ...(preset?.aliases || []),
        ...(preset?.asset?.aliases || []),
      ]
      const imageSources = getGlobalImageSources(notification)

      addTarget({
        id: `notification-${gameId}-${notification.uid}`,
        type: "notification",
        gameIndex,
        notificationIndex,
        gameId,
        gameName,
        label,
        triggerEventName: notification.triggerEventName,
        trackingName: `${gameTrackingName}-${eventTrackingName}`,
        aliases: eventAliases,
        gameAliases: [],
        placementAliases: eventAliases,
        isConfigured: true,
        currentImage: imageSources[0]?.src,
        pixels: notification.trackingPixels,
      })
    })

    addTarget({
      id: `launchGameBanner-${gameId}`,
      type: "launchGameBanner",
      gameIndex,
      gameId,
      gameName,
      label: "Launch Game Banner",
      trackingName: `${gameTrackingName}-LaunchGameBanner`,
      aliases: ["launch game banner", "launch screen banner", "launch banner", "launch"],
      gameAliases: [],
      placementAliases: ["launch game banner", "launch screen banner", "launch banner"],
      isConfigured: Boolean(gameConfig.launchGameBanner),
      currentImage: gameConfig.launchGameBanner?.image,
      pixels: gameConfig.launchGameBanner?.trackingPixels,
    })

    addTarget({
      id: `endgameBanner-${gameId}`,
      type: "endgameBanner",
      gameIndex,
      gameId,
      gameName,
      label: "Match End Banner",
      trackingName: `${gameTrackingName}-MatchEndBanner`,
      aliases: ["match end banner", "end screen banner", "endgame banner", "match end", "end"],
      gameAliases: [],
      placementAliases: ["match end banner", "end screen banner", "endgame banner"],
      isConfigured: Boolean(gameConfig.endgameBanner),
      currentImage: gameConfig.endgameBanner?.image,
      pixels: gameConfig.endgameBanner?.trackingPixels,
    })

    return targets
  })
}

export const getTargetLabel = (target: SpecialEventPlacementTarget): string => {
  return `${target.gameName} / ${target.label}`
}

export const findTargetsForFileName = (
  fileName: string,
  targets: SpecialEventPlacementTarget[],
): SpecialEventPlacementTarget[] => {
  const baseName = fileName.split(/[\\/]/).pop() || fileName
  const normalizedFileName = normalizeToken(baseName
    .replace(/(?:\.[a-z0-9]+)+$/gi, "")
    .replace(/\s*\(\d+\)\s*$/g, ""))
  const containsKnownGame = targets.some(target => (
    target.gameAliases.some(alias => containsPhrase(normalizedFileName, alias))
  ))
  const isGlobalAsset = normalizedFileName.startsWith("global ") && !containsKnownGame
  const candidates = targets.flatMap(target => {
    if (isGlobalAsset && target.type === "notification") return []
    const matchingGameAliases = isGlobalAsset
      ? ["global"]
      : target.gameAliases.filter(alias => containsPhrase(normalizedFileName, alias))
    if (matchingGameAliases.length === 0) return []

    const gameAlias = [...matchingGameAliases].sort((left, right) => right.length - left.length)[0]
    const gameAliasIndex = normalizedFileName.lastIndexOf(gameAlias)
    const placementText = normalizedFileName
      .slice(gameAliasIndex + gameAlias.length)
      .replace(/^\s*\d+\s+/, "")
      .replace(/\bglobal\b/g, " ")
      .replace(/\s+\d+\s*x\s*\d+$/, "")
      .replace(/\s+(?:v\d+)(?:\s+\d+)*$/, "")
      .trim()
    const matchingPlacementAliases = target.placementAliases
      .map(normalizeToken)
      .filter(alias => alias === placementText)

    if (matchingPlacementAliases.length === 0) return []

    const bestAliasLength = Math.max(...matchingPlacementAliases.map(alias => alias.length))

    return [{ target, score: bestAliasLength }]
  }).filter((candidate): candidate is { target: SpecialEventPlacementTarget; score: number } => Boolean(candidate))

  if (candidates.length === 0) return []
  const highestScore = Math.max(...candidates.map(candidate => candidate.score))
  const bestTargets = candidates.filter(candidate => candidate.score === highestScore).map(candidate => candidate.target)

  return isGlobalAsset || bestTargets.length === 1 ? bestTargets : []
}

export const upsertInternalPixel = (pixels: PixelEntry[] | null | undefined, generatedPixel: Pixel): Pixel[] => {
  const externalPixels = normalizePixels(pixels).filter(pixel => !isInternalPixel(pixel))

  return [generatedPixel, ...externalPixels]
}

export const appendMissingPixels = (pixels: PixelEntry[] | null | undefined, pixelsToAppend: Pixel[]): Pixel[] => {
  const existing = normalizePixels(pixels)
  const existingUrls = new Set(existing.map(pixel => pixel?.src).filter(Boolean))
  const missing = pixelsToAppend.filter(pixel => pixel?.src && !existingUrls.has(pixel.src))

  return [...existing, ...missing]
}
